/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { Else, If, Then } from "react-if";
import { Box, Text, theme } from "@nordcloud/gnui";
import {
  CloudResource,
  CloudResourcesSortFields,
  CloudResourcesSortInput,
  SortOrder,
  CloudResourcesFilterByFields,
  CloudResourcesFilterByRangeFields,
  CloudResourcesFilterByKeyValueFields,
  KeyValueFilterComparisonOperator,
  RangeFilterComparisonOperator,
  Maybe,
  CloudAccount,
} from "~/generated/graphql";
import { LoaderWrap, NoData } from "~/components";
import { transformStringifiedTagToKeyValueObject } from "~/components/MultiTagsInput";
import { Pagination } from "~/components/Pagination";
import { TableWrapper } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotNil, isNotEmpty } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { useGetCloudResources } from "~/views/resources_v2/hooks";
import { ResourceQuery } from "~/views/resources_v2/types";
import { VMsOnlyFilter } from "~/views/resources_v2/utils";
import { ResourceCtxProvider } from "../ResourceProvider";
import { ResourceTableState } from "./const";
import { ResourceListHeader } from "./ResourceListHeader";
import { columns } from "./ResourcesColumns";

const isDiscoveryTimeRange = (queryState: ResourceQuery): boolean => {
  return (
    isNotNil(queryState.discoveryTimeStartUTC) &&
    isNotEmpty(queryState.discoveryTimeStartUTC) &&
    isNotNil(queryState.discoveryTimeEndUTC) &&
    isNotEmpty(queryState.discoveryTimeEndUTC)
  );
};

const isLifeSpanRange = (queryState: ResourceQuery): boolean => {
  return (
    isNotNil(queryState.resourceLifeSpanFrom) &&
    isNotNil(queryState.resourceLifeSpanTo)
  );
};

// eslint-disable-next-line complexity
const prepareFilterBy = (queryState: ResourceQuery, resourceType: string) => {
  const vmsFilter =
    resourceType && resourceType !== "All"
      ? createFilterByInput<CloudResourcesFilterByFields>(
          CloudResourcesFilterByFields.ResourceType,
          VMsOnlyFilter,
          false
        )
      : [];

  const cloudServicesFilter = queryState.cloudServices
    ? createFilterByInput<CloudResourcesFilterByFields>(
        CloudResourcesFilterByFields.CloudService,
        queryState.cloudServices,
        queryState.excludeCloudServices ?? false
      )
    : [];

  const regionsFilter = queryState.regions
    ? createFilterByInput<CloudResourcesFilterByFields>(
        CloudResourcesFilterByFields.Region,
        queryState.regions,
        queryState.excludeRegions ?? false
      )
    : [];

  const resourceTypes = queryState.resourceTypes
    ? createFilterByInput<CloudResourcesFilterByFields>(
        CloudResourcesFilterByFields.ResourceType,
        queryState.resourceTypes,
        queryState.excludeResourceTypes ?? false
      )
    : [];

  const cloudProviders = queryState.cloudProviders
    ? createFilterByInput<CloudResourcesFilterByFields>(
        CloudResourcesFilterByFields.Provider,
        queryState.cloudProviders,
        queryState.excludeCloudProviders ?? false
      )
    : [];

  const cloudAccounts = queryState.cloudAccounts
    ? createFilterByInput<CloudResourcesFilterByFields>(
        CloudResourcesFilterByFields.Provider,
        queryState.cloudAccounts,
        queryState.excludeCloudAccounts ?? false
      )
    : [];

  const lastPatchingStatuses = queryState.lastPatchingStatuses
    ? createFilterByInput<CloudResourcesFilterByFields>(
        CloudResourcesFilterByFields.LastPatchingStatus,
        queryState.lastPatchingStatuses,
        queryState.excludeLastPatchingStatuses ?? false
      )
    : [];

  const retVal = [
    ...vmsFilter,
    ...cloudServicesFilter,
    ...regionsFilter,
    ...resourceTypes,
    ...cloudProviders,
    ...cloudAccounts,
    ...lastPatchingStatuses,
  ];

  return isEmpty(retVal) ? undefined : retVal;
};

const prepareRangeFilterBy = (queryState: ResourceQuery) => {
  const discoveryTime = isDiscoveryTimeRange(queryState)
    ? [
        {
          field: CloudResourcesFilterByRangeFields.DiscoveryTime,
          operator: queryState.excludeDiscoveryTimeRange
            ? RangeFilterComparisonOperator.Ne
            : RangeFilterComparisonOperator.Eq,
          from: queryState.discoveryTimeStartUTC,
          to: queryState.discoveryTimeEndUTC,
        },
      ]
    : [];

  const lifeSpanFilter = isLifeSpanRange(queryState)
    ? [
        {
          field: CloudResourcesFilterByRangeFields.ResourceLifespan,
          operator: queryState.excludeResourceLifeSpan
            ? RangeFilterComparisonOperator.Ne
            : RangeFilterComparisonOperator.Eq,
          from: `${queryState.resourceLifeSpanFrom}`,
          to: `${queryState.resourceLifeSpanTo}`,
        },
      ]
    : [];

  const retVal = [...discoveryTime, ...lifeSpanFilter];

  return isEmpty(retVal) ? undefined : retVal;
};

const prepareKeyValueFilterBy = (queryState: ResourceQuery) => {
  const resourceTagsFilter =
    isNotNil(queryState.resourceTagNamesAndTagValues) &&
    isNotEmpty(queryState.resourceTagNamesAndTagValues ?? [])
      ? [
          {
            field: CloudResourcesFilterByKeyValueFields.Tag,
            operator: queryState.excludeResourceTagNamesAndTagValues
              ? KeyValueFilterComparisonOperator.Ne
              : KeyValueFilterComparisonOperator.Eq,
            values: queryState.resourceTagNamesAndTagValues?.map((tag) =>
              transformStringifiedTagToKeyValueObject(tag)
            ),
          },
        ]
      : [];

  const resourceMetadataFilter =
    isNotNil(queryState.resourceMetadataKeysAndValues) &&
    isNotEmpty(queryState.resourceMetadataKeysAndValues ?? [])
      ? [
          {
            field: CloudResourcesFilterByKeyValueFields.Tag,
            operator: queryState.excludeResourceMetadataKeysAndValues
              ? KeyValueFilterComparisonOperator.Ne
              : KeyValueFilterComparisonOperator.Eq,
            values: queryState.resourceMetadataKeysAndValues?.map((metadata) =>
              transformStringifiedTagToKeyValueObject(metadata)
            ),
          },
        ]
      : [];

  const retVal = [...resourceTagsFilter, ...resourceMetadataFilter];

  return isEmpty(retVal) ? undefined : retVal;
};

export function ResourcesTable() {
  const [resourcesIds, setResourcesIds] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [resourceType, setResourceType] = useState<string>("All");
  const [allResourcesSelected, setAllResourcesSelected] = useState(false);
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();
  const selectionResetKey = useMemo(
    () => JSON.stringify({ ...queryState, page: undefined }),
    [queryState]
  );

  const showUnassigned = queryState.allocation === "unassigned";

  const [resourcesSearchPage, setResourcesSearchPage] =
    useState<ResourceTableState>({
      resources: [],
      loading: false,
    });

  useEffect(() => {
    updateQueryState({ page: "1" }, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType]);

  const sortInput: CloudResourcesSortInput = {
    field: queryState.field ?? CloudResourcesSortFields.Name,
    order: queryState.order ?? SortOrder.Asc,
  };
  const filters = {
    filterBy: prepareFilterBy(queryState, resourceType),
    keyValueFilterBy: prepareKeyValueFilterBy(queryState),
    rangeFilterBy: [
      ...(prepareRangeFilterBy(queryState) ?? []),
      ...(showUnassigned
        ? [
            {
              field: CloudResourcesFilterByRangeFields.Unassigned,
              operator: RangeFilterComparisonOperator.Eq,
            },
          ]
        : []),
    ],
  };

  const { data, loading, count } = useGetCloudResources({
    page: queryState.page,
    limit: queryState.limit,
    sort: sortInput,
    filter: filters,
  });

  const resourcePage = { resources: data, count, loading };
  const resources = useMemo(
    () => resourcePage.resources?.filter(isNotNil) ?? [],
    [resourcePage.resources]
  );
  const resourcesDetails = useMemo(
    () =>
      resources.filter((item) =>
        resources.some((resource) => resource.id === item?.id)
      ),
    [resources]
  );

  const cleanRowSelection = () => {
    setRowSelection({});
    setAllResourcesSelected(false);
    setResourcesIds([]);
  };

  useEffect(() => {
    cleanRowSelection();
  }, [selectionResetKey]);

  useEffect(() => {
    if (!allResourcesSelected) {
      return;
    }

    setRowSelection((previousSelection) => {
      const resourcesToSelect = resources.filter(
        (resource) => !previousSelection[resource.id]
      );

      if (resourcesToSelect.length === 0) {
        return previousSelection;
      }

      return resourcesToSelect.reduce<RowSelectionState>(
        (selection, resource) => ({
          ...selection,
          [resource.id]: true,
        }),
        previousSelection
      );
    });
  }, [allResourcesSelected, resources]);

  const totalResources = count ?? 0;
  const pageResources = resources.length;
  const selectedOnPage = resources.filter((resource) =>
    Boolean(rowSelection[resource.id])
  ).length;
  const allOnPageSelected =
    selectedOnPage > 0 &&
    selectedOnPage === pageResources &&
    !allResourcesSelected;

  const selectAllResources = () => {
    setAllResourcesSelected(true);
    setResourcesIds([]);
  };

  return (
    <ResourceCtxProvider value={{ cleanRowSelection }}>
      <ResourceListHeader
        resourcesDetails={resourcesDetails}
        setResourcesSearchPage={setResourcesSearchPage}
        resourceType={resourceType}
        setResourceType={setResourceType}
        selectedResourceIDs={resourcesIds}
        allResourcesSelected={allResourcesSelected}
        filters={filters}
      />
      {(allOnPageSelected || allResourcesSelected) && (
        <Box boxStyle="lightGrey" style={{ border: "1px solid black" }}>
          {allResourcesSelected ? (
            <Text style={{ whiteSpace: "nowrap", lineHeight: "1.5" }}>
              All {totalResources} Resources are selected.{" "}
              <Text
                color="primary"
                style={{
                  display: "inline",
                  textDecoration: "underline",
                  cursor: "pointer",
                  marginLeft: "0.5rem",
                  verticalAlign: "baseline",
                  color: theme.color.interactive.link,
                }}
                onClick={cleanRowSelection}
              >
                Clear selection
              </Text>
            </Text>
          ) : (
            <Text style={{ whiteSpace: "nowrap", lineHeight: "1.5" }}>
              All {pageResources} Resources on this page are selected.{" "}
              <Text
                color="primary"
                style={{
                  display: "inline",
                  textDecoration: "underline",
                  cursor: "pointer",
                  marginLeft: "0.5rem",
                  verticalAlign: "baseline",
                  color: theme.color.interactive.link,
                }}
                onClick={selectAllResources}
              >
                Select all {totalResources} Resources across all pages.
              </Text>
            </Text>
          )}
        </Box>
      )}
      <LoaderWrap loading={loading} inContent>
        <If condition={isEmpty(resources)}>
          <Then>
            <NoData hasIcon message={"There are no Resources."} />
          </Then>
          <Else>
            <TableWrapper<CloudResourceWithPartialAccount>
              loading={resourcesSearchPage.loading || loading}
              data={resources}
              columns={columns()}
              selectedRowsHandler={setResourcesIds}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              stripped
            />
            <Pagination count={resourcePage.count ?? 0} />
          </Else>
        </If>
      </LoaderWrap>
    </ResourceCtxProvider>
  );
}

type CloudResourceWithPartialAccount = Omit<CloudResource, "cloudAccount"> & {
  cloudAccount?: Maybe<Pick<CloudAccount, "id" | "name">>;
};
